import { Dialog } from "@headlessui/react";
import { useCallback, useRef, useState } from "react";
import { MOBILE_BREAKPOINT } from "../../config";
import { Platform } from "../../graphql/generated";
import styled, { css } from "../../styles";
import { Card } from "../Card";
import { CloseIcon } from "../CloseIcon";
import { Button } from "../CTA";
import { Flex } from "../Flex";
import { Underline } from "../Tabs";
import { Text } from "../Text";
import { View } from "../View";
import { ActivityFeed } from "./ActivityFeed";
import { CommentForm } from "./CommentForm";
import { MediaPlayer, MediaPlayerHandle } from "./MediaPlayer";
import { ActivityItem, MediaItem } from "./types";

// Utility function to parse links and mentions in captions
const parseCaption = (text: string, platform?: Platform) => {
  const parts: Array<{
    type: "text" | "mention" | "url";
    content: string;
    href?: string;
  }> = [];

  // Regex patterns for different types of content
  const patterns = {
    mention: /@[a-zA-Z0-9_.]+/g,
    url: /https?:\/\/[^\s]+/g,
  };

  let lastIndex = 0;
  const allMatches: Array<{
    type: string;
    match: string;
    index: number;
    href?: string;
  }> = [];

  // Find all mentions
  let match;
  while ((match = patterns.mention.exec(text)) !== null) {
    const username = match[0].slice(1); // Remove the @
    const href =
      platform === Platform.Instagram
        ? `https://www.instagram.com/${username}/`
        : platform === Platform.Tiktok
        ? `https://www.tiktok.com/@${username}`
        : undefined;

    allMatches.push({
      type: "mention",
      match: match[0],
      index: match.index,
      href,
    });
  }

  // Reset regex
  patterns.mention.lastIndex = 0;

  // Find all URLs
  while ((match = patterns.url.exec(text)) !== null) {
    allMatches.push({
      type: "url",
      match: match[0],
      index: match.index,
      href: match[0],
    });
  }

  // Sort matches by index
  allMatches.sort((a, b) => a.index - b.index);

  // Build parts array
  allMatches.forEach((matchObj) => {
    // Add text before this match
    if (matchObj.index > lastIndex) {
      parts.push({
        type: "text",
        content: text.slice(lastIndex, matchObj.index),
      });
    }

    // Add the match
    parts.push({
      type: matchObj.type as "mention" | "url",
      content: matchObj.match,
      href: matchObj.href,
    });

    lastIndex = matchObj.index + matchObj.match.length;
  });

  // Add any remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: "text",
      content: text.slice(lastIndex),
    });
  }

  return parts;
};

export enum MediaReviewType {
  BookingPostSubmission = "bookingPostSubmission",
  ContentBriefDeliverable = "contentBriefDeliverable",
}

interface MediaReviewDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  media: MediaItem;
  type: MediaReviewType;
  activities?: ActivityItem[];
  creator?: {
    preferredProfile?: {
      avatar?: string | null;
      username?: string | null;
    } | null;
  } | null;
  platform?: Platform;
  onApprove: () => void;
  onAddComment: (comment: string, timestamp?: number) => void;
  onActivityUpdate: () => void;
}

export const MediaReviewDialog = ({
  isOpen,
  setIsOpen,
  media,
  type,
  activities = [],
  creator,
  platform,
  onApprove,
  onAddComment,
  onActivityUpdate,
}: MediaReviewDialogProps) => {
  const commentInputRef = useRef<HTMLInputElement>(null);
  const mediaPlayerRef = useRef<MediaPlayerHandle>(null);
  const sidePanelRef = useRef<HTMLDivElement>(null);
  const [currentTime, setCurrentTime] = useState(0);

  const scrollToCommentAndFocus = useCallback(() => {
    if (!commentInputRef.current || !sidePanelRef.current) return;

    const input = commentInputRef.current;
    const container = sidePanelRef.current;

    const inputRect = input.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const isInputVisible =
      inputRect.top >= containerRect.top &&
      inputRect.bottom <= containerRect.bottom;

    if (!isInputVisible) {
      input.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }

    setTimeout(
      () => {
        input.focus();
      },
      isInputVisible ? 0 : 300
    );
  }, []);

  const handleAddComment = useCallback(
    (comment: string, timestamp?: number) => {
      if (!onAddComment) return;
      onAddComment(comment, timestamp);
      onActivityUpdate?.();
    },
    [onAddComment, onActivityUpdate]
  );

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
      <Backdrop aria-hidden="true">
        <StyledDialogPanel>
          <CenteredContainer maxWidth={600} noPadding={true}>
            <AbsoluteWrap onClick={() => setIsOpen(false)}>
              <MobileOnly>
                <CloseWrap>
                  <CloseIcon width={28} height={28} />
                </CloseWrap>
              </MobileOnly>
              <DesktopOnly>
                <CloseIcon width={28} height={28} />
              </DesktopOnly>
            </AbsoluteWrap>

            <ReviewContainer>
              <MediaPlayer
                ref={mediaPlayerRef}
                media={media}
                aspectRatio={media.aspectRatio}
                onTimeUpdate={(t) => setCurrentTime(t)}
              />

              <ReviewSidePanel>
                <SidePanelContainer ref={sidePanelRef}>
                  <ActivitySection>
                    {type === MediaReviewType.BookingPostSubmission ? (
                      <View>
                        <View margin="0 0 xxl">
                          <Header>
                            <Text size="l" weight="bold" margin="0 0 0">
                              Review Draft
                            </Text>
                            <Underline selected={true} />
                          </Header>
                        </View>
                        <RenderCaption
                          caption={media.mediaCaption}
                          creator={creator}
                          platform={platform}
                        />
                      </View>
                    ) : (
                      <View margin="0 0 xxl">
                        <Header>
                          <Text size="l" weight="bold" margin="0 0 0">
                            Activity
                          </Text>
                          <Underline selected={true} />
                        </Header>
                      </View>
                    )}
                    <ActivityFeed
                      activities={activities}
                      mediaType={media.mediaType}
                      seekToTime={(t: number) =>
                        mediaPlayerRef.current?.seekTo(t)
                      }
                    />

                    <CommentForm
                      ref={commentInputRef}
                      media={media}
                      currentTime={currentTime}
                      onAddComment={handleAddComment}
                      onFocus={scrollToCommentAndFocus}
                    />
                  </ActivitySection>
                </SidePanelContainer>

                <ReviewActions>
                  <Flex align="center" justify="center" direction="column">
                    <Button buttonType="primary" margin="0" onClick={onApprove}>
                      Approve
                    </Button>
                  </Flex>
                </ReviewActions>
              </ReviewSidePanel>
            </ReviewContainer>
          </CenteredContainer>
        </StyledDialogPanel>
      </Backdrop>
    </Dialog>
  );
};

// Styled components
const ReviewContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: center;
  height: min(85vh, 900px);
  max-height: min(85vh, 900px);
  max-width: 100%;
  border-radius: ${(p) => p.theme.misc.borderRadius};
  overflow: hidden;
  background-color: #000; /* Ensure black background fills any gaps */

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    min-height: 100vh;
    max-height: none;
    height: auto;
    width: 100vw;
    max-width: 100vw;
    border-radius: 0;
  }
`;

const ReviewSidePanel = styled.div`
  display: flex;
  flex-direction: column;
  width: 440px;
  height: 100%;
  justify-content: flex-start;
  position: relative;
  background: ${(p) => p.theme.color.card.background};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    height: auto;
  }
`;

const ReviewActions = styled.div`
  padding: ${(p) => p.theme.spacing.m};
  border-top: 1px solid ${(p) => p.theme.color.card.divider};
  background-color: ${(p) => p.theme.color.card.background};
  box-sizing: border-box;
  margin-top: auto;
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 ${(p) => p.theme.spacing.m};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0;
  }
`;

const StyledDialogPanel = styled(Dialog.Panel)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  max-width: 95%;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-width: 100%;
    width: 100%;
    height: 100%;
  }
`;

const CenteredContainer = styled(Card)<{
  maxWidth?: number;
  noPadding?: boolean;
}>`
  display: flex;
  align-items: flex-start;
  margin: 0 auto;
  overflow-y: auto;
  position: relative;
  outline: none !important;
  max-height: 90vh;
  width: 100%;
  box-shadow: none !important;

  [data-headlessui-state="open"] *:focus {
    outline: none !important;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-height: 100vh;
    height: 100vh;
    width: 100vw;
    max-width: 100vw;
    margin: 0;
    border-radius: 0;
  }

  ${(p) =>
    p.noPadding
      ? css`
          padding: 0;
        `
      : css`
          padding: ${(p) => p.theme.spacing.xl};

          @media (max-width: ${MOBILE_BREAKPOINT}px) {
            padding: 0;
          }
        `}
`;

const AbsoluteWrap = styled.div`
  position: absolute;
  top: ${(p) => p.theme.spacing.m};
  right: ${(p) => p.theme.spacing.m};
  cursor: pointer;
  z-index: 2;

  svg {
    path {
      fill: ${(p) => p.theme.color.typography.text};

      &:hover {
        fill: ${(p) => p.theme.color.typography.heading};
    }
  }
`;

const SidePanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  background: ${(p) => p.theme.color.card.callout};
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(p) => p.theme.color.typography.secondary}30;
    border-radius: 10px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${(p) => p.theme.color.typography.secondary}50;
    opacity: 1;
  }

  &:hover::-webkit-scrollbar-thumb,
  &:active::-webkit-scrollbar-thumb {
    opacity: 1;
  }

  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &:hover,
  &:active {
    scrollbar-color: ${(p) => p.theme.color.typography.secondary}30 transparent;
  }
`;

const ActivitySection = styled.div`
  padding: ${(p) => p.theme.spacing.m} ${(p) => p.theme.spacing.l}
    ${(p) => p.theme.spacing.l};
`;

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  background-color: ${(p) => p.theme.color.card.callout};
  padding: ${(p) => p.theme.spacing.m} ${(p) => p.theme.spacing.l} 0;
`;

const RenderCaption = ({
  caption,
  creator,
  platform,
}: {
  caption?: string;
  creator?: {
    preferredProfile?: {
      avatar?: string | null;
      username?: string | null;
    } | null;
  } | null;
  platform?: Platform;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const maxLength = 100; // Character limit before showing "more" button
  const shouldTruncate = caption && caption.length > maxLength;
  const textToDisplay = !caption
    ? ""
    : isExpanded || !shouldTruncate
    ? caption
    : caption.slice(0, maxLength);

  // Parse the text for links, hashtags, and mentions
  const parsedParts = caption ? parseCaption(textToDisplay, platform) : [];

  const renderParsedText = () => {
    if (!caption || caption.trim().length === 0) {
      return (
        <span style={{ color: "inherit", opacity: 0.6 }}>
          No caption provided
        </span>
      );
    }

    return parsedParts.map((part, index) => {
      if (part.type === "text") {
        // Handle newlines by splitting and adding <br/> elements
        const lines = part.content.split("\n");
        return (
          <span key={index}>
            {lines.map((line, lineIndex) => (
              <span key={lineIndex}>
                {line}
                {lineIndex < lines.length - 1 && <br />}
              </span>
            ))}
          </span>
        );
      }

      if (part.href) {
        return (
          <LinkText
            key={index}
            href={part.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {part.content}
          </LinkText>
        );
      }

      return <span key={index}>{part.content}</span>;
    });
  };

  return (
    <CaptionContainer>
      <Flex direction="row" align="flex-start" gap="m">
        <CaptionTextContainer>
          <CaptionText>
            {creator?.preferredProfile?.username && (
              <UsernameText
                href={
                  platform === Platform.Tiktok
                    ? `https://www.tiktok.com/@${creator.preferredProfile?.username}`
                    : `https://www.instagram.com/${creator.preferredProfile?.username}`
                }
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {creator.preferredProfile.username}
              </UsernameText>
            )}
            {renderParsedText()}
            {caption && shouldTruncate && !isExpanded && (
              <span
                style={{
                  display: "inline-block",
                  letterSpacing: 2,
                  marginLeft: 2,
                }}
              >
                ...{" "}
                <MoreButton onClick={() => setIsExpanded(true)}>
                  more
                </MoreButton>
              </span>
            )}
          </CaptionText>
        </CaptionTextContainer>
      </Flex>
    </CaptionContainer>
  );
};

// Instagram-like caption styled components
const CaptionContainer = styled.div`
  padding-top: ${(p) => p.theme.spacing.s};
  padding-bottom: ${(p) => p.theme.spacing.xl};
  border-bottom: 1px solid ${(p) => p.theme.color.divider};
`;

const CaptionTextContainer = styled.div`
  flex: 1;
`;

const CaptionText = styled.div`
  font-size: 14px;
  line-height: 1.4;
  color: ${(p) => p.theme.color.typography.text};
`;

const MoreButton = styled.button`
  background: none;
  border: none;
  color: ${(p) => p.theme.color.typography.secondary};
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  text-decoration: none;

  &:hover {
    color: ${(p) => p.theme.color.typography.text};
  }
`;

const LinkText = styled.a`
  color: ${(p) => p.theme.color.typography.link};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const UsernameText = styled.a`
  color: ${(p) => p.theme.color.typography.text};
  font-weight: ${(p) => p.theme.typography.weight.bold};
  text-decoration: none;
  cursor: pointer;
  margin-right: ${(p) => p.theme.spacing.xs};

  &:hover {
    text-decoration: underline;
  }
`;

const CloseWrap = styled.div`
  background-color: ${(p) => p.theme.color.card.background};
  border-radius: 999px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${(p) => p.theme.shadow.imageFloatingIcon};
  transition: all 0.15s cubic-bezier(0.32, 0.72, 0, 1);
  transform: scale(1);

  &:hover {
    filter: brightness(0.95);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover,
    &:active {
      transform: none;
    }
  }
`;

const MobileOnly = styled.div`
  display: none;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: block;
  }
`;

const DesktopOnly = styled.div`
  display: none;

  @media (min-width: ${MOBILE_BREAKPOINT}px) {
    display: block;
  }
`;
